import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import BaseModal from 'shared/components/base-modal'
import { COMMUNITY_ACCESS_DENIED_EVENT } from 'shared/constants'
import { AccessDeniedReasonsEnum } from 'shared/errors/access-denied'
import LockIcon from 'shared/icons/lock-icon'
import ThinArrowRightIcon from 'shared/icons/thin-arrow-right-icon'

interface CommunityAccessDeniedEvent extends CustomEvent {
  detail: { reason?: string }
}

export function CommunityOwnerBlockedModal(): ReactNode {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleAccessDenied = (event: Event): void => {
      const customEvent = event as CommunityAccessDeniedEvent
      if (customEvent.detail?.reason === AccessDeniedReasonsEnum.CommunityOwnerBlocked) {
        setIsOpen(true)
      }
    }

    globalThis.addEventListener(COMMUNITY_ACCESS_DENIED_EVENT, handleAccessDenied)

    return () => {
      globalThis.removeEventListener(COMMUNITY_ACCESS_DENIED_EVENT, handleAccessDenied)
    }
  }, [])

  return (
    <BaseModal opened={isOpen} onClose={() => {}} small>
      <div className="relative flex flex-col items-center px-6 py-10 text-center lg:px-16 lg:py-14">
        <div className="mb-6 rounded-full bg-THEME-disabled p-5">
          <LockIcon className="size-12 fill-text-tertiary" />
        </div>
        <h2 className="mb-3 text-xl font-semibold text-text-primary">
          {t('community.owner_blocked_modal.title', 'Community unavailable')}
        </h2>
        <p className="mb-8 text-text-secondary">
          {t(
            'community.owner_blocked_modal.description',
            'This community is no longer accessible. Contact the owner for more information',
          )}
        </p>
        <a
          href="/community"
          className="group inline-flex items-center gap-2 rounded-md border border-transparent bg-brand-primary px-6 py-3 font-medium text-white transition-colors duration-300 hover:border hover:border-brand-primary hover:bg-white hover:text-brand-primary"
        >
          {t('community.owner_blocked_modal.explore_button', 'Explore Other Communities')}
          <ThinArrowRightIcon className="size-5 -rotate-45 stroke-background-secondary transition-colors group-hover:stroke-brand-primary" />
        </a>
      </div>
    </BaseModal>
  )
}
